// ── THE FULL STRATEGY REPORT — THE DOCUMENT A GROWER HOLDS ───────────────────
//
// Renders the view model reportSynthesis.js builds. It decides nothing and computes
// nothing: every figure and every sentence is already on the model, and this file's
// only job is hierarchy — the recommendation first, large, and the evidence after it.
//
// Four sheets, each answering one part of the sequence Kevin set (2026-09-16):
//
//     1  Recommended strategy       what, what it costs, why — per county
//     2  Current vs proposed        what actually changes, side by side
//     3  Why we recommend it        the history behind it, as charts
//     4  Other decisions & needs    what we still need, FSA, futures context
//
// followed, only when asked for, by the Detailed Coverage Analysis appendix.
//
// ⚠ IT IS NOT A SHRUNK V1. DU MOND AG LLC's V1 printed 15 sheets. That was not solved
// with smaller type — the recommendation figures here are LARGER — but by moving the
// engine's working into the appendix and saying each thing once.
//
// ⚠ ITS OWN CLASSES, ALL PREFIXED `v2-`. The document is written into a popup whose
// sheet-wide stylesheet (WHEAT_PRINT_CSS) right-aligns every td and gives `.page`
// break-inside:avoid; a V2 sheet borrows none of that.

import { money, PENDING, titleCounty } from "./reportSynthesis.js";

const esc = (t) => String(t == null ? "" : t).replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
// ⚠ A MISSING FIGURE PRINTS AS "Pending", NEVER AS $0.
const m$ = (v, o) => { const s = money(v, o); return s == null ? `<span class="v2-pend">${PENDING}</span>` : esc(s); };

export const STRATEGY_REPORT_CSS = `
  /* ⚠ THE PAGE MARGIN IS DECLARED HERE, NOT INHERITED. The app's popup sets 0.5in through
     WHEAT_PRINT_CSS, and a render of this document without it had ~0.1in more room per
     edge — enough that Dumond's page one fitted in the preview and spilled "Important for
     2027" onto a sheet of its own in the app. */
  @page { size: letter; margin: 0.5in; }
  .v2 { color:#15261a; font-family: -apple-system, "Segoe UI", Arial, sans-serif; }
  .v2 * { box-sizing:border-box; }
  /* ⚠ BREAK BEFORE, NOT AFTER. The sheets live in several wrappers (the standard report,
     the appendix divider, the technical notes), and a ":last-child { break-after:auto }"
     rule — written so the document would not end on a blank sheet — also fired on the
     last sheet of the STANDARD report, so the appendix divider printed its header at the
     foot of page four and shared page five with the ARC/PLC table. Every sheet but the
     document's first now starts a page, and the divider also ends one. */
  .v2-sheet { page-break-before:always; break-before:page; padding:0; }
  .v2-sheet.v2-first { page-break-before:auto; break-before:auto; }
  .v2-appx { page-break-after:always; break-after:page; }
  .v2 table { width:100%; border-collapse:collapse; font-size:10pt; }
  .v2 th, .v2 td { text-align:left; padding:0; border:0; background:none; text-transform:none; letter-spacing:0; font-weight:inherit; color:inherit; }
  .v2-top { display:flex; align-items:center; justify-content:space-between; border-bottom:2px solid #2d6a2d; padding-bottom:5px; }
  .v2-top img { height:32px; width:auto; display:block; }
  .v2-agency { font-size:7pt; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:#60735f; text-align:right; line-height:1.4; }
  .v2-kicker { font-size:8.5pt; font-weight:800; letter-spacing:.12em; text-transform:uppercase; color:#3f8f45; margin:11px 0 1px; }
  .v2-h1 { font-size:20pt; font-weight:800; line-height:1.1; margin:0; color:#10241a; }
  .v2-sub { font-size:9.5pt; color:#5f705f; margin-top:2px; line-height:1.4; }
  .v2-h2 { font-size:14pt; font-weight:800; margin:12px 0 2px; color:#10241a; }
  .v2-draft { margin:10px 0 0; border:2px solid #b3381f; background:#fdf0ec; color:#7a2413; border-radius:8px; padding:8px 12px; font-size:9.5pt; }
  .v2-draft b { display:block; font-size:10.5pt; margin-bottom:2px; }
  .v2-card { border:1px solid #d4e3d4; border-radius:10px; margin-top:10px; overflow:hidden; break-inside:avoid; page-break-inside:avoid; background:#fff; }
  .v2-card-h { display:flex; align-items:center; justify-content:space-between; background:#eff6ef; padding:6px 12px; }
  .v2-county { font-size:12pt; font-weight:800; letter-spacing:.04em; text-transform:uppercase; color:#1a3a22; }
  .v2-chip { font-size:8pt; font-weight:800; border-radius:20px; padding:2px 10px; white-space:nowrap; }
  .v2-chip.chg { background:#1f5c2e; color:#fff; }
  .v2-chip.keep { background:#fff; color:#1f5c2e; border:1.5px solid #9cc49c; }
  .v2-card-b { padding:8px 12px 9px; }
  .v2-four { display:flex; gap:8px; }
  .v2-four > div { flex:1; min-width:0; }
  .v2-four > div.v2-base { flex:1.35; }
  .v2-four > div.v2-money { background:#f4f9f4; border-radius:7px; padding:4px 8px 5px; }
  .v2-lbl { font-size:7.5pt; font-weight:700; letter-spacing:.07em; text-transform:uppercase; color:#6f836f; }
  .v2-big { font-size:14pt; font-weight:800; color:#10241a; line-height:1.2; margin-top:1px; }
  .v2-money .v2-big { color:#1f5c2e; }
  .v2-line { font-size:10pt; font-weight:700; color:#10241a; line-height:1.3; margin-top:2px; }
  .v2-small { font-size:8.5pt; color:#5f705f; margin-top:1px; line-height:1.35; }
  .v2-down { color:#8a5a00 !important; }
  .v2-why { margin-top:7px; font-size:9.5pt; line-height:1.42; color:#22331f; }
  .v2-why p { margin:0 0 3px; }
  .v2-why b { color:#10241a; }
  .v2-pend { color:#8a6d1f; font-weight:700; }
  .v2-guar { display:flex; gap:2px 14px; flex-wrap:wrap; margin-top:5px; padding:3px 8px; border:1px solid #e3ece3; border-radius:7px; font-size:8.5pt; color:#22331f; line-height:1.3; }
  .v2-guar > div { white-space:nowrap; }
  .v2-guar > div.aph { white-space:normal; }
  .v2-guar b { color:#10241a; }
  .v2-guar .k { font-size:7pt; font-weight:700; letter-spacing:.06em; text-transform:uppercase; color:#6f836f; margin-right:4px; }
  .v2-guar .sub { color:#5f705f; }
  .v2-imp { margin-top:10px; border-top:2px solid #e3ece3; padding-top:5px; break-inside:avoid; page-break-inside:avoid; }
  .v2-imp-grid { display:flex; flex-wrap:wrap; gap:4px 16px; margin-top:3px; }
  .v2-imp-grid > div { flex:1 1 45%; min-width:0; border-bottom:1px solid #eef3ee; padding:2px 0 4px; }
  .v2-imp .k { font-size:8pt; color:#5f705f; font-weight:700; }
  .v2-imp .v { font-size:9.5pt; font-weight:800; color:#10241a; line-height:1.25; }
  .v2-imp .n { font-size:8.5pt; color:#5f705f; line-height:1.3; }
  .v2-cmp { border:1px solid #d4e3d4; border-radius:10px; margin-top:10px; padding:8px 12px 9px; break-inside:avoid; page-break-inside:avoid; }
  .v2-cmp-h { display:flex; align-items:center; justify-content:space-between; gap:8px; flex-wrap:wrap; }
  .v2-diff { display:flex; gap:6px; flex-wrap:wrap; }
  .v2-diff div { background:#10241a; color:#fff; border-radius:6px; padding:3px 9px; font-size:9pt; font-weight:700; white-space:nowrap; }
  .v2-diff div span { font-weight:500; opacity:.8; margin-left:4px; }
  .v2-cmp-cols { display:flex; gap:10px; margin-top:6px; }
  .v2-cmp-cols > div { flex:1; min-width:0; border-radius:8px; padding:6px 10px 5px; }
  .v2-cur { background:#f5f7f5; } .v2-prop { background:#edf6ed; box-shadow: inset 0 0 0 1.5px #9cc49c; }
  .v2-colh { font-size:8pt; font-weight:800; letter-spacing:.08em; text-transform:uppercase; }
  .v2-cur .v2-colh { color:#6f7f6f; } .v2-prop .v2-colh { color:#1f5c2e; }
  .v2-kv td { padding:2.5px 0; font-size:9.5pt; border-bottom:1px solid rgba(0,0,0,.05); }
  .v2-kv td.r { text-align:right; font-weight:800; }
  .v2-kv tr:last-child td { border-bottom:0; }
  .v2-ladder { position:relative; height:14px; background:#fff; border:1px solid #d9e3d9; border-radius:4px; margin:6px 0 13px; }
  .v2-ladder .seg { position:absolute; top:0; bottom:0; }
  .v2-ladder .ind { background:#2d6a2d; } .v2-ladder .sco { background:#86b886; } .v2-ladder .eco { background:#bcd9bc; }
  .v2-ladder .tick { position:absolute; top:15px; font-size:7pt; color:#6f7f6f; white-space:nowrap; }
  .v2-prior { font-size:8.5pt; color:#6f7f6f; margin-top:5px; }
  /* ── SHORT REPORT · page one ──────────────────────────────────────────────── */
  /* ⚠ TWO CARDS MUST FIT A LETTER SHEET. Measured by rendering Meisenzahl: at the
     first cut each card ran a page on its own and the report came to five, over the
     four-page maximum. The savings are vertical padding and the bar block, not type
     size — the figures a grower reads are deliberately large. */
  .s1-card { border:1px solid #d4e3d4; border-radius:10px; margin-top:6px; padding:6px 12px 7px; break-inside:avoid; page-break-inside:avoid; }
  .s1-head { display:flex; align-items:baseline; justify-content:space-between; gap:10px; }
  .s1-county { font-size:13pt; font-weight:800; color:#14361f; letter-spacing:.01em; }
  .s1-acres { font-size:8.5pt; color:#6f7f6f; }
  .s1-grid { width:100%; border-collapse:collapse; margin-top:7px; table-layout:fixed; }
  .s1-grid th { font-size:7.5pt; font-weight:800; letter-spacing:.07em; text-transform:uppercase; color:#6f836f; padding:0 6px 3px; text-align:right; }
  .s1-grid th.l { text-align:left; width:27%; }
  .s1-grid th.prop { color:#1f5c2e; }
  .s1-grid td { padding:3.5px 6px; font-size:11pt; text-align:right; border-top:1px solid #eef3ee; font-variant-numeric:tabular-nums; }
  .s1-grid td.l { text-align:left; font-size:9pt; color:#55675a; font-weight:600; }
  .s1-grid td.prop { font-weight:800; color:#14361f; }
  .s1-grid td.chg { font-weight:700; color:#1f5c2e; }
  .s1-grid td.chg.dn { color:#8a5a00; }
  .s1-grid td.el { font-size:9.5pt; }
  .s1-rec { margin-top:8px; border-radius:8px; background:#f2f8f2; border:1px solid #cfe3cf; padding:8px 12px 9px; }
  .s1-rec-k { font-size:7.5pt; font-weight:800; letter-spacing:.09em; text-transform:uppercase; color:#1f5c2e; }
  .s1-rec-v { font-size:15pt; font-weight:800; color:#14361f; line-height:1.15; margin-top:1px; }
  .s1-act { font-size:9.5pt; font-weight:700; color:#1f5c2e; margin-top:1px; }
  .s1-rec-p { font-size:11pt; font-weight:700; color:#1f5c2e; margin-top:2px; }
  .s1-why { font-size:9pt; color:#55675a; margin-top:4px; }
  .s1-foot { font-size:8pt; color:#7d8c7d; margin-top:4px; }
  /* The coverage stack leads the card. Centred, with the figure's own caption
     suppressed — the page header already names the year and the county. */
  .s1-stack { margin:2px 0 4px; text-align:center; }
  .s1-stack svg { max-width:100%; height:auto; }
  .s1-bars { display:flex; gap:12px; margin-top:6px; }
  .s1-bars .v2-ladder { margin:4px 0 11px; height:12px; }
  .s1-bars > div { flex:1; min-width:0; }
  .s1-bl { font-size:7.5pt; font-weight:800; letter-spacing:.07em; text-transform:uppercase; color:#6f836f; }
  .s1-bl.prop { color:#1f5c2e; }
  .s1-bw { font-size:8pt; color:#6f7f6f; margin-top:15px; }
  .s1-key { font-size:7.5pt; color:#6f7f6f; margin-top:2px; display:flex; gap:10px; align-items:center; }
  .s1-key i { display:inline-block; width:9px; height:9px; border-radius:2px; margin-right:3px; vertical-align:-1px; }
  .v2-ev { border:1px solid #d4e3d4; border-radius:10px; margin-top:10px; padding:8px 12px 9px; break-inside:avoid; page-break-inside:avoid; }
  .v2-steps { margin-top:5px; }
  .v2-steps td, .v2-steps th { padding:3px 6px; font-size:9.5pt; border-bottom:1px solid #eef3ee; }
  .v2-steps th { font-size:7.5pt; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:#6f836f; }
  .v2-steps .n { text-align:right; }
  .v2-res { font-weight:700; }
  .v2-res.good { color:#1f5c2e; } .v2-res.meh { color:#8a5a00; } .v2-res.no { color:#8a3a1f; }
  .v2-inc { font-size:7pt; font-weight:800; color:#fff; background:#2d6a2d; border-radius:3px; padding:1px 5px; margin-left:5px; vertical-align:1px; }
  .v2-yrs { display:flex; align-items:flex-end; gap:4px; height:56px; margin-top:6px; border-bottom:1px solid #cfdccf; }
  .v2-yr { flex:1; display:flex; flex-direction:column; align-items:center; justify-content:flex-end; height:100%; }
  .v2-yr .bar { width:100%; max-width:34px; border-radius:2px 2px 0 0; background:#d7e4d7; }
  .v2-yr .bar.paid { background:#d08a2a; }
  .v2-yr .amt { font-size:7pt; font-weight:800; color:#8a5a00; line-height:10px; }
  .v2-yrl { display:flex; gap:4px; }
  .v2-yrl div { flex:1; text-align:center; font-size:7pt; color:#6f7f6f; }
  .v2-note { font-size:8.5pt; color:#6f7f6f; margin-top:6px; line-height:1.4; }
  .v2-need { border:1.5px solid #e2cf8f; background:#fdf8e8; border-radius:10px; padding:8px 12px; margin-top:8px; break-inside:avoid; page-break-inside:avoid; }
  .v2-need h3 { margin:0 0 2px; font-size:11pt; color:#5e4a10; }
  .v2-need p { margin:0; font-size:9.5pt; color:#5e4a10; line-height:1.42; }
  .v2-need ul { margin:5px 0 0; padding-left:18px; }
  .v2-need li { font-size:9.5pt; color:#3f3210; margin:1px 0; }
  .v2-elect { border:1.5px solid #bcd7bc; background:#f4faf4; border-radius:10px; padding:6px 11px 7px; margin-top:6px; break-inside:avoid; page-break-inside:avoid; }
  .v2-elect-h { font-size:8pt; font-weight:800; letter-spacing:.05em; text-transform:uppercase; color:#2d5a2d; }
  .v2-elect-lead { font-size:10.5pt; color:#1a2e1a; margin:2px 0 4px; line-height:1.3; }
  .v2-elect-t td, .v2-elect-t th { font-size:9pt; padding:2px 6px; vertical-align:top; }
  .v2-elect-t td.k { white-space:nowrap; font-weight:700; color:#2d4a2d; width:1%; }
  .v2-elect-why { margin:4px 0 0; padding-left:18px; }
  .v2-elect-why li { font-size:9pt; color:#2d3d2d; margin:1px 0; line-height:1.34; }
  /* ⚠ THE CLOSING LINES TRAVEL WITH THE LAST BOX. Page four is the one sheet whose length
     depends on the farm — every request in "What we still need" and every county adds to
     it — so it is the sheet that can run over. When it does, the market box, the legend
     and the footer move together, so the next sheet carries content and a grower is never
     handed a page that is only a disclaimer. (Measure pagination with a capture from a
     real build or with tools/fsr-rerender.js, which carries the app's print CSS — an
     earlier re-render without it paginated a full sheet differently.) */
  .v2-tail { break-inside:avoid; page-break-inside:avoid; }
  .v2-box { border:1px solid #d4e3d4; border-radius:10px; padding:8px 12px 9px; margin-top:8px; break-inside:avoid; page-break-inside:avoid; }
  .v2-box h3 { margin:0; font-size:11pt; color:#10241a; }
  .v2-tbl { margin-top:5px; }
  .v2-tbl th { font-size:7.5pt; font-weight:800; letter-spacing:.06em; text-transform:uppercase; color:#6f836f; padding:3px 6px; border-bottom:1.5px solid #d4e3d4; }
  .v2-tbl td { font-size:9.5pt; padding:4px 6px; border-bottom:1px solid #eef3ee; }
  .v2-tbl .n { text-align:right; }
  .v2-foot { margin-top:10px; font-size:8pt; color:#60735f; line-height:1.45; border-top:1px solid #e3ece3; padding-top:6px; }
  .v2-legend { font-size:8pt; color:#6f7f6f; margin-top:10px; line-height:1.5; }
  .v2-appx .v2-h1 { font-size:24pt; }
  .v2-appx p { max-width:5in; margin:10px 0 0; font-size:10.5pt; color:#5f705f; line-height:1.5; }
  @media screen {
    .v2 { max-width:8.5in; margin:0 auto; padding:0 14px; }
    .v2-sheet { border-bottom:1px dashed #cfdccf; padding-bottom:18px; margin-bottom:18px; }
  }
  @media screen and (max-width: 640px) {
    .v2 { padding:0 10px; }
    .v2-four, .v2-cmp-cols { display:block; }
    .v2-four > div, .v2-cmp-cols > div { margin-top:8px; }
    .v2-h1 { font-size:18pt; }
    .v2-steps th:nth-child(2), .v2-steps td:nth-child(2) { display:none; }
  }
`;

function top(logoSrc) {
  return `<div class="v2-top">${logoSrc ? `<img src="${esc(logoSrc)}" alt="Clover Ag Risk Management"/>` : "<span></span>"}
    <div class="v2-agency">Clover Ag Risk Management<br/>Spartan Insurance Agency LLC</div></div>`;
}
function scopeName(r, multiState) {
  return esc(r.county) + " County" + (multiState && r.state ? ", " + esc(r.state) : "") + (r.practiceLabel ? " · " + esc(r.practiceLabel) : "");
}
function draftBanner(vm) {
  const issues = [...(vm.blocking || []), ...((vm.contradictions || []).map((c) => c.message))];
  if (!issues.length) return "";
  return `<div class="v2-draft"><b>Draft — check before handing this to the grower</b>${issues.map((m) => esc(m)).join("<br/>")}</div>`;
}

// ── SHEET 1 · RECOMMENDED STRATEGY ───────────────────────────────────────────
// The one premium change, split into the layers that move — each priced as proposed.
function splitLine(sp) {
  if (!sp || !(sp.parts || []).length) return "";
  return sp.parts.map((p) => `<div class="v2-small">${esc(p.label)} ${esc(money(p.amount, { perAcre: true, signed: true }))}</div>`).join("");
}
// A unit RMA has not priced has nothing to compare, so it is one line on every sheet
// rather than a full card of "Pending" — measured on R L JEFFRES & SONS, whose organic
// Wyoming card pushed page one's "Important" block onto a sheet of its own.
function pendingLine(r, vm) {
  return `<div class="v2-card"><div class="v2-card-h"><div class="v2-county">${scopeName(r, vm.multiState)}</div><span class="v2-chip keep">${esc(r.status)}</span></div>
    <div class="v2-card-b"><div class="v2-small">${esc(r.reasons.why || "")}</div></div></div>`;
}
// ── THE GUARANTEE STRIP ─ what the grower is buying, in bushels and dollars ──
// Every figure is the quote page's own (reportSynthesis guaranteeFor): nothing here
// multiplies an APH. "Same" where the level does not move, so a keep reads as a keep.
const bu$ = (v) => (v == null ? `<span class="v2-pend">${PENDING}</span>` : esc(Number(v).toFixed(1)));
function guarStrip(r) {
  const g = r.guarantee;
  if (!g) return "";
  const same = (a, b) => a != null && b != null && Math.abs(a - b) < 0.05;
  const pair = (a, b, fmt, unit) => (a == null && b == null ? fmt(null)
    : g.isNew ? fmt(b) + unit
    : same(a, b) ? fmt(b) + unit + ' <span class="sub">(same)</span>'
    : fmt(a) + " → " + fmt(b) + unit);
  const rev$ = (v) => (v == null ? `<span class="v2-pend">${PENDING}</span>` : esc(money(v)));
  const price = g.price != null ? ' <span class="sub">at ' + esc(money(g.price)) + "/bu</span>" : "";
  const basis = g.aph.basis ? ' <span class="sub">(' + esc(g.aph.basis) + ")</span>" : "";
  return `<div class="v2-guar">
      <div><span class="k">Production guarantee</span>${pair(g.current.bu, g.proposed.bu, bu$, " bu/ac")}</div>
      <div><span class="k">Revenue guarantee</span>${pair(g.current.revenue, g.proposed.revenue, rev$, "/ac")}${price}</div>
      <div class="aph">${g.aph.pending ? `Approved yield — <span class="v2-pend">${PENDING}</span>` : esc(g.aph.line)}${basis}</div>
      ${yieldContextLine(g.aph)}
    </div>`;
}
function importantBlock(vm) {
  if (!(vm.important || []).length) return "";
  return `<div class="v2-imp"><div class="v2-lbl">Important for ${esc(vm.cropYear)}</div><div class="v2-imp-grid">
    ${vm.important.map((x) => `<div><div class="k">${esc(x.label)}</div><div class="v">${esc(x.value)}</div>${x.note ? `<div class="n">${esc(x.note)}${x.detail ? " · " + esc(x.detail) : ""}</div>` : ""}</div>`).join("")}
  </div></div>`;
}
// ⚠ THE RETIRED RENDERERS ARE DELETED, NOT LEFT DEFINED (2026-09-22).
//
//   recCard      page one’s old per-county card  -> `s1Card`
//   cmpBlock     the current-vs-proposed columns   -> page one’s own grid
//   sheet2       “What changes”                    -> page one
//   needsBlock   “What we still need”              -> an AGENT’s problem, not a
//                                                     grower’s; the dependencies it
//                                                     named are stated where they
//                                                     are used (the ARC/PLC sheet
//                                                     names the FSA payment yield)
//   marketBlock  the futures-context strip         -> the cash-target page
//   sheet4       “Other decisions”                 -> nothing calls it
//
// They stayed DEFINED for one commit after the rebuild and nothing called them, so
// sixteen mutations of them scored as MISSED — the suite was mutating code that could
// not reach a page. That is the same defect as the dead `recommend85` rule this repo
// removed the same day: **a renderer nothing calls cannot be tested, and a mutation
// that cannot fail is indistinguishable from a rule that cannot break.** The rules
// they carried are asserted on the surfaces that replaced them — check-strategy-report
// is 408 assertions over the four pages a grower actually receives.

// ── SHORT REPORT · PAGE ONE — LAST YEAR -> PROPOSED -> CHANGE ────────────────
//
// Kevin, 2026-09-22: *"The farmer should understand the insurance recommendation
// in about 15 seconds."* So the page is a grid of four numbers per county and one
// large recommendation, and every sentence that is not a number had to earn its
// place. The long report said the same thing five times per county over eighteen
// pages; this says it once.
//
// ⚠ THE CHANGE COLUMN CROSSES CROP YEARS AND SAYS SO. Last year is a BILL at last
// year's rates and prices; this year is an ESTIMATE at this year's. That is the
// comparison a grower actually wants and it is not the one that isolates the
// coverage decision — `comparison.diff` is, and page two keeps it.
const chgCell = (v, fmt, invert) => {
  if (v == null) return `<td class="chg">&mdash;</td>`;
  if (Math.abs(v) < 0.005) return `<td class="chg" style="color:#7d8c7d">no change</td>`;
  const worse = invert ? v > 0 : v < 0;
  return `<td class="chg${worse ? " dn" : ""}">${v > 0 ? "+" : "−"}${fmt(Math.abs(v))}</td>`;
};
const bu1 = (v) => Number(v).toFixed(1) + " bu";
const dol = (v) => "$" + Number(v).toFixed(2);
// ⚠ THE SHARE RIDES WITH THE BAND. SCO and ECO are bought at 50-100% OF the band,
// and "ECO" printed flat for a grower holding half of it describes coverage he does
// not have — 11 CY2026 wheat growers hold ECO at 50% and one at 75%. `share$` is the
// same helper both other surfaces use, so page one cannot describe an endorsement
// differently from the comparison beneath it.
// The GRID row is the full description — bands and shares — because that row is
// where a grower checks what he carried against what is proposed. The HEADLINE is
// the short form, because Kevin's whole instruction for the recommendation box was
// that it reads in about fifteen seconds. Both come from the same side object, so
// they cannot describe different coverage.
function electionText(side, bands) {
  if (!side || side.level == null) return PENDING;
  const area = (side.sco || side.eco) ? " · " + areaLine(side, bands) : " · no county coverage";
  return side.level + "% RP" + area;
}
function electionShort(side) {
  if (!side || side.level == null) return PENDING;
  const p = [side.level + "% RP"];
  if (side.sco) p.push("SCO" + share$(side.scoPct));
  if (side.eco) p.push("ECO" + share$(side.ecoPct));
  return p.join(" + ");
}
function s1Card(r, vm) {
  const c = r.comparison || {};
  const ly = c.lastYear || {}, pt = c.proposedTotal || {}, vs = c.vsLastYear || {};
  // ⚠ EACH YEAR ON ITS OWN LATTICE. SCO's top and ECO's bottom moved 86 -> 90 for
  // 2027, so one shared band set paints last year's ECO at half its true height.
  const b26 = (r.bands && r.bands.prior) || null, b27 = (r.bands && r.bands.quote) || null;
  const acres = r.acreageText ? esc(r.acreageText) : "";
  // No approved yield under it, no guarantee figure on either side — see the row below.
  const aphPending = !!(r.guarantee && r.guarantee.aph && r.guarantee.aph.pending);
  const why = (r.reasons && (r.reasons.whyNotHigher || r.reasons.why)) || null;
  // ⚠ THE STACK LEADS (Kevin, 2026-09-23: "1.) Coverage Stack 2.) Compare to last
  // year …"). The bars were BELOW the four-row grid, so the page opened on a table
  // and the stack — the thing the page is now named for — arrived after it. They
  // are the same bars, in the same place in the card's flow relative to everything
  // else; only the grid moved under them. The grid STAYS: page two is the detailed
  // side-by-side and it does not build for a farm with no prior year, so stripping
  // the summary here would leave new business with no premium figure anywhere.
  // ⚠ THE STACK IS THE PAGE, AND IT IS NOT REBUILT HERE. `r.stackHtml` is the print
  // twin of the on-screen CoverageStackViz, emitted by the printer that priced the
  // layers — so it carries what each of MPCI, SCO and ECO costs and covers. The two
  // ladders beneath it are the BEFORE-AND-AFTER, which the stack cannot show: it
  // draws one election, and the question on this page is what changes. They are the
  // same bars that have been on page one since 2026-09-22, moved under the stack.
  // ⚠ THE LEVEL COMPARISON REPLACES BOTH, IT DOES NOT SIT BESIDE THEM. Kevin,
  // 2026-09-23: "I want the attached coverage stack like the attached screenshot in
  // the FSR" — the screenshot is the quote sheet's COVERAGE LEVEL COMPARISON, a bar
  // per coverage level with each layer's premium and coverage printed in it. It
  // already contains everything the two things below it were drawing: its first bar
  // is LAST YEAR'S ACTUAL, and every 2027 bar carries the coverage and premium delta
  // against the recommended one. Drawn together they are the same comparison twice —
  // the duplication that pushed a one-county card onto a second sheet once already
  // (1,022px against 960px of letter, 2026-09-23) and the reason the money rows moved
  // into the stack. So the single stack and the before/after ladders are the FALLBACK,
  // for a card the printer could not price level by level.
  const bars = r.barsHtml
    ? `<div class="s1-stack">${r.barsHtml}</div>`
    : `${r.stackHtml ? `<div class="s1-stack">${r.stackHtml}</div>` : ""}
    <div class="s1-bars">
      <div><div class="s1-bl">Last year (${esc(ly.year || vm.cropYear - 1)})</div>
        ${ly.notPlanted || ly.level == null
          ? `<div class="s1-bw">${ly.notPlanted ? "No wheat planted" : PENDING}</div>`
          : ladder(ly.level, ly, b26)}</div>
      <div><div class="s1-bl prop">Proposed (${esc(vm.cropYear)})</div>
        ${c.proposed && c.proposed.level != null ? ladder(c.proposed.level, c.proposed, b27) : `<div class="s1-bw">${PENDING}</div>`}</div>
    </div>`;
  return `<div class="s1-card">
    <div class="s1-head"><div class="s1-county">${scopeName(r, vm.multiState)}</div>
      <div class="s1-acres">${acres}</div></div>
    ${bars}
    <table class="s1-grid"><thead><tr>
      <th class="l"></th>
      <th>Last year (${esc(ly.year || vm.cropYear - 1)})</th>
      <th class="prop">Proposed (${esc(vm.cropYear)})</th>
      <th>Change</th></tr></thead><tbody>
      <tr><td class="l">Coverage</td>
        <td class="el">${ly.notPlanted ? `<span style="color:#7d8c7d">no wheat planted</span>` : esc(electionText(ly, b26))}</td>
        <td class="el prop">${esc(electionText(c.proposed, b27))}</td>
        ${/* ⚠ COMPARE THE ELECTION, NOT THE BAND TEXT. SCO's top and ECO's bottom moved
              86 -> 90 for 2027, so the descriptions legitimately differ for a grower
              who changed nothing — and comparing them printed "changed" on every
              unchanged renewal in the book. What the column reports is whether HIS
              election moved. */ ""}
        <td class="chg" style="color:#7d8c7d">${ly.notPlanted ? "new" : electionShort(ly) === electionShort(c.proposed) ? "same" : "changed"}</td></tr>
      ${/* ⚠ THE GUARANTEE ROW DEFERS TO `guarantee`, NOT TO `comparison`. Every figure
            here is APH x level x price, so with NO APPROVED YIELD there is no
            guarantee — `guaranteeFor` nulls both sides and says "pending". The
            comparison object still computes a bushel figure from the proposed level,
            and printing that put 52.5 -> 56.0 bu/ac on a card whose own note said the
            approved yield was pending. One authority, or the page contradicts itself. */ ""}
      <tr><td class="l">Production guarantee</td>
        <td>${aphPending || ly.guaranteeBu == null ? "&mdash;" : esc(bu1(ly.guaranteeBu)) + "/ac"}</td>
        <td class="prop">${aphPending || pt.guaranteeBu == null ? "&mdash;" : esc(bu1(pt.guaranteeBu)) + "/ac"}</td>
        ${aphPending ? `<td class="chg">&mdash;</td>` : chgCell(vs.guaranteeBu, bu1)}</tr>
      ${/* ⚠ THE MONEY IS THE STACK'S JOB — WHEN THERE IS A STACK. These two rows are
            the stack's own Total line, and the stack prints them BROKEN DOWN BY LAYER:
            a reader can see what SCO costs and what it buys, which a total never
            showed. Kept unconditionally they were the same two figures twice on one
            page, and the pair of them was what pushed a one-county card onto a second
            sheet (measured 1,022px against 960px of letter, 2026-09-23).
            ⚠ BUT THEY ARE A FALLBACK, NOT A DELETION, and the checker is what caught
            that: on a card with no stack — `stackHtml` is null wherever the printer
            could not price the layers — dropping them left page one with NO coverage
            and NO premium anywhere on it. A page that silently loses its money is
            worse than one that repeats it.
            The CHANGE in these figures is on page two either way; the side-by-side
            carries every line of it. */ ""}
      ${/* ⚠ THE TEST IS "DID A PER-LAYER CHART DRAW", NOT "IS THERE A STACK". Either
            chart prints the money broken down by layer, so either one makes these two
            rows the same figures twice. Keyed on `stackHtml` alone this still reads
            correctly today — a bar row cannot exist without a stack, both need the
            same priced quote — but that is a coincidence of two conditions, not a
            rule, and the rows it controls are the only coverage and premium figures
            on page one when neither chart draws. */ ""}
      ${r.stackHtml || r.barsHtml ? "" : `<tr><td class="l">Total coverage</td>
        <td>${ly.coverageTotal == null ? "&mdash;" : esc(dol(ly.coverageTotal)) + "/ac"}</td>
        <td class="prop">${pt.coverageTotal == null ? "&mdash;" : esc(dol(pt.coverageTotal)) + "/ac"}</td>
        ${chgCell(vs.coverageTotal, dol)}</tr>
      <tr><td class="l">Producer premium</td>
        <td>${ly.premium == null ? "&mdash;" : esc(dol(ly.premium)) + "/ac"}</td>
        <td class="prop">${pt.premium == null ? "&mdash;" : esc(dol(pt.premium)) + "/ac"}</td>
        ${chgCell(vs.premium, dol, true)}</tr>`}
    </tbody></table>
    ${/* The colour key is printed ONCE at the foot of the page, not under every card —
          it is the same three colours each time, and per-card it cost a line that put
          a two-county farm onto a fifth page. */ ""}
    <div class="s1-rec">
      <div class="s1-rec-k">Proposed</div>
      <div class="s1-rec-v">${esc(electionShort(c.proposed))}</div>
      ${/* ⚠ THE ELECTION IS NOT THE INSTRUCTION. "75% RP + SCO + ECO" is what he ends
            up with; "Keep 75% · Add SCO (75–90%) · Keep ECO (90–95%)" is what to DO,
            and the difference matters most on the county that is gaining a layer —
            the grid shows SCO appearing between two columns, which a reader has to
            spot. These are the engine's own action sentences, not a second wording. */ ""}
      <div class="s1-act">${esc([r.base && r.base.text].concat((r.area && r.area.lines) || []).filter(Boolean).join(" · "))}</div>
      ${/* The LABEL never disappears — only the figure. A row that vanishes when a
            premium is withheld reads as a page that forgot to price the county, which
            is the opposite of what withholding is for. */ ""}
      ${r.priceMissing ? "" : `<div class="s1-rec-p">Estimated premium ${pt.premium == null ? `<span class="v2-pend">${PENDING}</span>`
        : esc(dol(pt.premium)) + "/ac" + (r.estimatedTotal && r.estimatedTotal.text ? " &middot; " + esc(r.estimatedTotal.text) : "")}</div>`}
      ${why ? `<div class="s1-why"><b>Why:</b> ${esc(why)}</div>` : ""}
    </div>
    ${(() => {
      // ⚠ THREE SHORT NOTES THAT ARE NOT DECORATION. A grower reading "60.8 bu/ac"
      // has to know it rests on LAST year's approved yield, and that last year's
      // dollars are at last year's rates and prices. Dropping them in the rebuild
      // made the comparison read as like-for-like when it is not — they were on the
      // retired sheet two, and they come back here as one line, not a paragraph.
      const notes = [];
      // ⚠ THE APPROVED YIELD AND HOW IT WAS BUILT. Every guarantee on this card is
      // APH x level x price, so which year's APH and whether it is a BLEND are facts
      // about the numbers above — a yield standing on 3 of 7 databases weighted by
      // last year's acres is not the same claim as one approved database. It was on
      // the retired guarantee strip and comes back as part of one line.
      // ⚠ THE RECORD THE RECOMMENDATION RESTS ON. The green box carries the reason a
      // grower acts on ("80% would cost another $8.38/ac…"); `reasons.why` is the
      // other half — how many years of HIS records that rests on, which for a
      // two-year county is the most important thing on the card. It goes in the note
      // rather than the box so the box stays one sentence, and only when it is not
      // already the sentence in the box.
      if (why && r.reasons && r.reasons.why && r.reasons.why !== why) notes.push(r.reasons.why);
      const g = r.guarantee, ga = g && g.aph;
      if (ga && ga.pending) notes.push("Approved yield — " + PENDING + ".");
      else if (ga && ga.line) notes.push(ga.line + (ga.basis ? " (" + ga.basis + ")" : ""));
      // ⚠ `aphNote` SAYS THE SAME THING AS `aph.line` AND IS DROPPED WHERE IT DOES.
      // The card printed "2026 approved yield: 81.0 bu · 2027 pending" immediately
      // followed by "Guarantees use your 2026 APH of 81 bu; your 2027 APH is not on
      // file yet." — one fact, twice, in the smallest type on the page. It is kept
      // only when the line above is absent, so nothing is lost on a card that has no
      // approved-yield line of its own.
      const aphSaid = notes.length > 0;
      if (ly.coverageDerived && !ly.notPlanted) notes.push("Last year's coverage is derived from RMA's published " + ly.year + " price; the premium shown is what you were billed.");
      if (c.aphNote && !aphSaid) notes.push(c.aphNote);
      // `priorNote` is the SOURCED sentence for both cases — the premium with what it
      // was for, or the AIP's own reason there was none ("no wheat was planted in
      // Wayne County in 2026"). It is not re-worded here; a second sentence saying
      // the same thing less precisely is how two surfaces come to disagree.
      if (c.priorNote) notes.push(c.priorNote);
      // ⚠ THE ONE SENTENCE THAT SAYS WHETHER THE COUNTY TRIGGER CAUGHT *THIS FARM'S*
      // SHORT YEARS, and it was computed and dropped for half a day. It lived on sheet
      // three, which the five-page rebuild removed (2026-09-23); page three is now the
      // county's settled record per year, which is more detail and does NOT make this
      // cross-reference — it reports what the county did, not whether it paid when the
      // farm was short. "In the years your yield fell below 80% of your APH (2017,
      // 2018, 2019, 2020, 2022, 2025), SCO and ECO would have paid only in 2019" is the
      // most decision-relevant thing on the page about area coverage, and page one is
      // where the SCO/ECO recommendation is made. Found by mutation testing: the
      // mutation that nulls `lossLine` applied cleanly and nothing failed.
      if (r.history && r.history.lossLine) notes.push(r.history.lossLine);
      // Each note is a sentence from a different producer and not all of them end in
      // a stop, so joining them ran "2027 pending For reference, your 2026 premium…"
      // together as one clause.
      const dot = (t) => (/[.!?]$/.test(String(t).trim()) ? String(t).trim() : String(t).trim() + ".");
      return notes.length ? `<div class="s1-foot">${esc(notes.map(dot).join(" "))}</div>` : "";
    })()}
  </div>`;
}
function sheet1(vm, o) {
  return `<section class="v2-sheet v2-first">${top(o.logoSrc)}
    <div class="v2-kicker">${esc(vm.title)}</div>
    <div class="v2-h1">${esc(vm.farm)}</div>
    ${/* ⚠ THE PRICE IS ON PAGE ONE WHATEVER THE COUNTY COUNT. The "Important for
          <year>" strip carries it, but from three priced counties that strip strands
          and opens page two — so on exactly the farms with the most coverage to
          compare, page one showed $441.74 -> $580.68 with no way to see that most of
          the move is the PRICE ($5.76 -> $7.54) rather than more coverage. It reads
          off the same `vm.important` entry, so the two cannot disagree. */ ""}
    <div class="v2-sub">What you carried, what we propose, and what changes${o.dateStr ? " · prepared " + esc(o.dateStr) : ""}.${(() => {
      const px = (vm.important || []).find((x) => /projected .*price/i.test(x.label || ""));
      return px ? " " + esc(px.label) + " " + esc(px.value) + "." : "";
    })()}</div>
    ${draftBanner(vm)}
    ${/* ⚠ KEPT, AND IT IS NOT DECORATION. This strip carries the projected price and
          the prevent-plant factor. Page one shows coverage moving $441.74 -> $580.68
          for Monroe, and MOST OF THAT IS THE PRICE ($5.76 -> $7.54), not a coverage
          change — without the price on the page the grower reads a coverage increase
          we did not give him. Two lines, not the "long prevented-plant explanation"
          that was cut. */ ""}
    ${/* ⚠ ALWAYS ON PAGE ONE SINCE THE FIVE-PAGE REBUILD (2026-09-23), and it had to
         become always. The strip moved to sheet two from two priced counties up, to
         stop it stranding alone on a sheet — and sheet two is GONE, so on exactly the
         farms with the most coverage to explain it would have vanished: the projected
         price, the prevent-plant factor change and the SCO/ECO band move with it.
         Stranding is not a risk here anyway. It renders BEFORE the county cards, so
         it is always followed by content; what stranded it was being the only thing
         on a page of its own. `importantOnSheet1` is retired rather than left as a
         switch nobody reads. */ ""}
    ${importantBlock(vm)}
    ${(vm.recommendations || []).map((r) => s1Card(r, vm)).join("")}
    <div class="s1-key"><span><i style="background:#2d6a2d"></i>your own coverage</span>
      <span><i style="background:#86b886"></i>SCO</span>
      <span><i style="background:#bcd9bc"></i>ECO</span>
      <span>as a share of your APH revenue basis</span></div>
    <div class="s1-foot">Last year is what was billed, at that year's rates and prices; the proposed column is a ${esc(vm.cropYear)} estimate. Premiums are producer premium per acre.</div>
  </section>`;
}
// ⚠ "IMPORTANT" BELONGS UNDER THE CARDS — UNTIL IT WOULD STRAND. Four priced county cards fill
// a letter sheet, and the block then printed ALONE on a sheet of its own, which reads as a
// blank page. From four priced counties it opens sheet two instead.
// ⚠ TWO SINCE THE GUARANTEE STRIP (2026-09-18). Each card gained a line of guarantee, and on
// DU MOND AG LLC's three-county page the block no longer fitted under three cards — it printed
// alone on sheet two, the stranded sheet this rule exists to prevent. Measured by rendering.
// ⚠ ONE, NOT TWO, SINCE THE 2026-09-22 REBUILD. Each county card gained a
// four-row grid, two coverage bars and a recommendation box, so two priced
// counties now fill a letter sheet on their own — Meisenzahl's Ontario card
// spilled onto a second page with the strip above it. The strip's most important
// figure, the projected price, is in the page header regardless, so moving it
// costs nothing a grower reads first. Measured by rendering, not by counting rows.
// ⚠ RETIRED 2026-09-23. `importantOnSheet1` decided WHICH SHEET the strip opened,
// and it sent it to SHEET TWO — which no longer exists. Left in place it would have
// made the strip DISAPPEAR on exactly the farms with the most coverage to explain.
// The strip is on page one unconditionally now, above the cards, where it is always
// followed by content and so cannot be the lone item on a sheet.

// ── SHEET 2 · CURRENT VS PROPOSED ────────────────────────────────────────────
// The bar is 0–100% of the farm's own revenue guarantee basis. The dark part is the
// farm's own coverage; the lighter parts are the county-based layers above it. Only the
// base level and the top of the stack are labelled — the bands between are named in
// the row beneath, and labelling 90 and 95 five points apart printed them on top of
// each other.
function ladder(level, flags, bands) {
  if (level == null) return "";
  const segs = [`<div class="seg ind" style="left:0;width:${level}%"></div>`];
  let topPct = level;
  if (bands && flags.sco) {
    const t = Math.round(bands.scoTop * 100);
    segs.push(`<div class="seg sco" style="left:${level}%;width:${Math.max(0, t - level)}%"></div>`);
    topPct = Math.max(topPct, t);
  }
  if (bands && flags.eco) {
    const lo = Math.round(bands.ecoBottom * 100), hi = Math.round(bands.ecoTop * 100);
    segs.push(`<div class="seg eco" style="left:${lo}%;width:${hi - lo}%"></div>`);
    topPct = Math.max(topPct, hi);
  }
  const tick = (at, align) => `<div class="tick" style="${align === "r" ? "right:" + (100 - at) + "%" : "left:" + at + "%;transform:translateX(-50%)"}">${at}%</div>`;
  return `<div class="v2-ladder">${segs.join("")}${tick(level, "c")}${topPct > level + 7 ? tick(topPct, "r") : ""}</div>`;
}
// The share rides with the band here too: page one and page two may not describe one
// endorsement two different ways.
const share$ = (v) => (v == null || Number(v) >= 100 - 1e-9 ? "" : " at " + (Number.isInteger(Number(v)) ? Number(v) : Number(v).toFixed(2)) + "%");
function areaLine(side, bands) {
  const p = [];
  if (side.sco && bands) p.push("SCO " + side.level + "–" + Math.round(bands.scoTop * 100) + "%" + share$(side.scoPct));
  if (side.eco && bands) p.push("ECO " + Math.round(bands.ecoBottom * 100) + "–" + Math.round(bands.ecoTop * 100) + "%" + share$(side.ecoPct));
  return p.length ? p.join(" + ") : "no county coverage";
}

// ── SHEET 3 · REMOVED 2026-09-23 ────────────────────────────────────────────
// `evBlock` and `sheet3` printed "Compared with last year — what each step of
// coverage costs": the step economics as a table, plus a SUMMARY of the SCO/ECO
// trigger record.
//
// Kevin's five-page report replaces both halves with something better. Page two is
// the real side-by-side and page three is the trigger record ITSELF — every settled
// year with the dollars it would have paid — so this page was a second, shorter
// account of the page beside it. The step economics are on page one, in the green
// box's own sentences, out of the same engine: "Moving to 85% would cost another
// $14.71/ac. It would have roughly paid for itself on your history…"
//
// ⚠ IT IS DELETED RATHER THAN LEFT UNCALLED. It called `importantOnSheet1`, which
// this rebuild retired, so it had become code that compiles and throws if anything
// ever calls it — the worst state to leave a page in. check-strategy-report asserts
// the step figures still reach the reader on page one, so the rule it enforced is
// still enforced somewhere.
// ── SHEET 4 · OTHER DECISIONS & INFORMATION NEEDED ───────────────────────────
// ── THE REPORT'S OWN ARC/PLC NARRATIVE IS GONE (Kevin, 2026-09-19) ───────────
// It printed the election a SECOND time, in the report's own words, above the farm election
// sheet the report now embeds — and it led with "Enroll in PLC if your payment yield is above
// 78 bu…", which is the shape Kevin had already rejected on the sheet: a call in bold with its
// alternative in a subordinate clause reads as a recommendation. Two surfaces stating one
// election is also how they come to disagree. The sheet is the election now; `fsaSection` still
// builds the view-model, because the "what we still need" ask reads its switch yields from it.
// Do not reinstate this block — add to the sheet instead.

export function appendixDividerHtml(logoSrc) {
  return `<section class="v2-sheet v2-appx">${top(logoSrc)}
    <div class="v2-kicker" style="margin-top:2.4in">Appendix</div>
    <div class="v2-h1">Detailed Coverage Analysis</div>
    <p>Every figure behind the recommendation: premium tables for each coverage level, the year-by-year county trigger record, prevented-planting calculations, the FSA program estimate, the futures reference levels, and the technical notes on how each coverage level was tested.</p>
  </section>`;
}

// ── APPENDIX: THE ENGINE'S OWN REASONING ─────────────────────────────────────
// The standard report translates; this page does not. It is where the leave-one-year-
// out resampling, the band trace and the classification live — the working an agent
// or a reviewer may need, and the one place in the document where the engine speaks
// in its own terms.
// ── CV AND BASIS, BESIDE THE DECISION THEY EXPLAIN ──────────────────────────
//
// Kevin, 2026-09-30: "Can we add CV and Basis to the print quote and full
// strategy report?" They sit under the classification line because they are the
// two facts that decide it: CV is the farm's own yield volatility (higher →
// own-farm MPCI level earns its keep) and basis is approved APH ÷ county
// expected (near 1.0 → the farm tracks its county, so SCO/ECO area coverage
// fits; far off → basis risk cuts its value). The engine's band walk above says
// WHAT it chose; these say what it was reading.
//
// ⚠ THE VALUES ARE READ, NEVER RE-DERIVED. They are computed once per unit in
// `wheatQuotePages` off that unit's own yield series and its own FIPS, and
// emitted into the fact sink. Re-deriving them here would need the production
// history and would reach for component state — which, in the BATCH printer
// that walks many farms through one document, is another grower's.
//
// ⚠ A MISSING CV IS "<3yr", NOT 0% AND NOT A DASH. `cvOf` returns null under
// three years of history, which is a different fact from a steady farm — and
// reading it as steady is the direction that talks somebody out of own-farm
// coverage. Basis is omitted entirely when the county expected yield is not
// known, rather than printed against nothing.
function yieldContextLine(u) {
  if (!u) return "";
  const bits = [];
  bits.push("CV " + (u.cvPct != null ? Number(u.cvPct).toFixed(0) + "%" : "&lt;3yr"));
  if (u.yieldBasis != null && u.countyExpBu != null) {
    bits.push("basis " + Number(u.yieldBasis).toFixed(2) + "&times;"
      + " (APH ÷ " + Math.round(Number(u.countyExpBu)) + " bu county expected)");
  } else if (u.countyExpBu != null) {
    bits.push("county expected " + Math.round(Number(u.countyExpBu)) + " bu");
  }
  if (bits.length < 2 && u.cvPct == null && u.countyExpBu == null) return "";
  return `<div class="v2-small">Yield context · ${bits.join(" · ")}</div>`;
}

export function technicalNotesHtml(units, logoSrc) {
  const rows = (units || []).filter((u) => u && u.engine);
  if (!rows.length) return "";
  const block = (u) => {
    const e = u.engine;
    const bd = e.bandDecision || {};
    const trace = (bd.trace || []).map((t) => `<li><b>${esc(t.rule)}</b>${t.band ? " " + esc(t.band) : ""}${t.detail ? " — " + esc(t.detail) : ""}</li>`).join("");
    const why = (e.why || []).map((w) => `<li>${esc(w)}</li>`).join("");
    return `<div class="v2-box"><h3>${esc(titleCounty(u.county))} County${u.practiceLabel ? " · " + esc(u.practiceLabel) : ""}</h3>
      <div class="v2-small">Classification ${esc(e.classification || "—")} · decided by ${esc(bd.decidedBy || "—")}${e.keepReason ? " · " + esc(e.keepReason) : ""}</div>
      ${yieldContextLine(u)}
      ${why ? `<div class="v2-lbl" style="margin-top:6px">Engine summary</div><ul class="v2-small" style="margin:2px 0 0;padding-left:16px">${why}</ul>` : ""}
      ${trace ? `<div class="v2-lbl" style="margin-top:6px">Band walk</div><ul class="v2-small" style="margin:2px 0 0;padding-left:16px">${trace}</ul>` : ""}
    </div>`;
  };
  return `<div class="v2"><section class="v2-sheet">${top(logoSrc)}
    <div class="v2-h2">Technical notes — how each coverage level was tested</div>
    <div class="v2-sub">The recommendation engine's own record, unedited. Each 5-point step is priced net of the county coverage it replaces, compared with its average payout on the farm's yield record, and re-tested with each year left out in turn.</div>
    ${rows.map(block).join("")}
  </section></div>`;
}

// The standard report, with the optional appendix after it. The standard report is
// wrapped in `data-standard-report` and the appendix in `data-appendix`, so the
// language check reads the customer pages only.
// ⚠ THE ELECTION SHEET HAS ITS OWN SLOT, BETWEEN THE CUSTOMER PAGES AND THE APPENDIX.
// It is the FSA decision, not supporting analysis: filed after `appendixDividerHtml` it printed
// under "Detailed Coverage Analysis — every figure behind the recommendation", which is the
// opposite of what it is. Its own slot also means a report with no appendix requested prints
// the sheet and NO divider, instead of a divider introducing a section of one page.
// ⚠ THE ORDER IS KEVIN'S, 2026-09-21: the synthesis pages, then the QUOTE exactly as the Print
// Quote button shows it, then the ARC/PLC election sheet, then the cash wheat targets — and only
// then the optional appendix. Each is its own slot rather than one concatenated blob, so a caller
// cannot silently reorder them and every checker can name the one it means.
export function strategyReportHtml(vm, { logoSrc = null, dateStr = null, bands2027 = null,
  quoteHtml = "", electionSheetHtml = "", comparePagesHtml = "", futuresLevelsHtml = "",
  cashTargetsHtml = "", appendixHtml = "" } = {}) {
  if (!vm || !(vm.recommendations || []).length) return "";
  const o = { logoSrc, dateStr, bands2027 };
  // ── FIVE PAGES (Kevin, 2026-09-23) ───────────────────────────────────────────
  //
  //   "Heres how I want the full strategy report: 1.) Coverage Stack 2.) Compare to
  //    last year. 3.) SCO/ESO trigger chart. 4.) The clear concise FSA election
  //    recommendation (if your payment yield is above or below x, enroll in x)
  //    Simple, not wordy. 5.) Futures level technical targets. Not recommendations
  //    to sell, or where to deliver or anything, just futures level targets based
  //    on a fundamental."
  //
  //   1  sheet1               the coverage stack, and what it costs
  //   2  comparePagesHtml     the side-by-side, per county
  //   3  comparePagesHtml     the county's settled SCO/ECO trigger record
  //   4  electionSheetHtml    the FSA ARC/PLC election
  //   5  futuresLevelsHtml    futures reference levels on the crop-year board
  //
  // ⚠ PAGES 2 AND 3 ARRIVE TOGETHER AND THAT IS DELIBERATE. `comparePagesHtml` is
  // the quote path's own builder asked for with `quoteSheet: false`, which emits a
  // county's side-by-side and then that county's trigger record — so a two-county
  // farm reads Livingston's compare, Livingston's record, Monroe's compare,
  // Monroe's record, rather than two compares followed by two records nobody can
  // pair up. They are the SAME pages Print Quote emits; a second implementation of
  // the side-by-side is how the grower's report and the agent's quote come to show
  // one farm two different comparisons.
  //
  // ⚠ WHAT LEFT, AND WHY IT IS NOT A LOSS:
  //   · `sheet3` ("Compared with last year — what each step costs") was the SUMMARY
  //     of a trigger record the report could not print. Page three is now that
  //     record itself, drawn per year with the dollars it would have paid, so the
  //     summary is a second, shorter account of the page beside it.
  //   · the CASH wheat targets page. Kevin: futures levels, "not recommendations to
  //     sell, or where to deliver or anything". A cash ladder is a delivery-point
  //     quote by construction — it converts at a basis posted by a named elevator —
  //     so it is the one page that cannot be made to comply by rewording. It still
  //     builds and still prints from the Cash Targets page, which is where an agent
  //     asks that question.
  // Neither is deleted from the codebase.
  return `<style>${STRATEGY_REPORT_CSS}</style>
  <div class="v2" data-standard-report="1">${sheet1(vm, o)}</div>
  ${comparePagesHtml ? `<div data-compare="1">${comparePagesHtml}</div>` : ""}
  ${electionSheetHtml ? `<div data-election-sheet="1">${electionSheetHtml}</div>` : ""}
  ${futuresLevelsHtml ? `<div data-futures-levels="1">${futuresLevelsHtml}</div>` : ""}
  ${quoteHtml ? `<div data-quote="1">${quoteHtml}</div>` : ""}
  ${cashTargetsHtml ? `<div data-cash-targets="1">${cashTargetsHtml}</div>` : ""}
  ${appendixHtml ? `<div class="v2">${appendixDividerHtml(logoSrc)}</div><div data-appendix="1">${appendixHtml}</div>` : ""}`;
}

// The customer-visible standard report only — what the language check reads.
export function standardReportSlice(html) {
  const s = String(html || "");
  const a = s.indexOf('data-standard-report="1"');
  if (a < 0) return "";
  const i = s.lastIndexOf("<", a);
  const j = s.indexOf('data-appendix="1"');
  return j > i ? s.slice(i, s.lastIndexOf("<", j)) : s.slice(i);
}
